// =============================================================================
// JSON-LD STRUCTURED DATA GENERATOR
// =============================================================================
// Generates schema.org JSON-LD structured data for SEO.
//
// WHAT IS JSON-LD?
// JSON-LD (JavaScript Object Notation for Linked Data) is a format that
// search engines (Google, Bing) and AI assistants (ChatGPT, Gemini, Perplexity)
// use to understand what a webpage is about. It's embedded in the page's <head>
// as a <script type="application/ld+json"> tag.
//
// WHY IT MATTERS FOR RESTAURANTS:
// - Makes the restaurant eligible for "rich results" in Google (stars, hours, price range)
// - Powers the Google Maps "knowledge panel" sidebar
// - Helps AI assistants answer "Where can I eat Italian food near me?"
// - The Menu schema makes individual dishes discoverable in search
//
// SCHEMA.ORG HIERARCHY:
// Organization → LocalBusiness → FoodEstablishment → Restaurant
// We use "Restaurant" (the most specific type) for best results.
// =============================================================================

import type { Restaurant, Menu, DeveloperConfig } from '@webzite/content-schema';

/**
 * Generates a Restaurant JSON-LD object for the homepage.
 *
 * This is the main structured data block that tells search engines:
 * "This website represents a restaurant at this address, with these hours,
 *  this cuisine, this price range, and this phone number."
 *
 * @param restaurant - The restaurant data from restaurant.json
 * @param siteUrl - The deployed URL (e.g., "https://trattoria-marco.com")
 * @param developer - Optional developer branding / attribution
 * @returns A JSON-LD object ready to stringify and embed in <script>
 */
export function generateRestaurantSchema(
  restaurant: Restaurant,
  siteUrl: string,
  developer?: DeveloperConfig,
): Record<string, unknown> {
  const devName = developer?.name ?? 'jeduapf';
  const devUrl = developer?.url ?? 'https://portfolio.jeduapf.com';
  const devRole = developer?.role ?? 'Restaurant Web Developer';

  return {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',

    // Creator / Web Developer
    creator: {
      '@type': 'Person',
      name: devName,
      url: devUrl,
      jobTitle: devRole,
      sameAs: [devUrl],
    },

    // Basic info
    name: restaurant.name,
    description: restaurant.tagline,
    servesCuisine: restaurant.cuisineTypes,
    priceRange: restaurant.priceRange,
    telephone: restaurant.phone,
    email: restaurant.email,
    url: siteUrl,

    // Hero image (if available in gallery)
    ...(restaurant.gallery.length > 0 && {
      image: restaurant.gallery[0]?.src,
    }),

    // Address (schema.org PostalAddress)
    address: {
      '@type': 'PostalAddress',
      streetAddress: restaurant.address.streetAddress,
      addressLocality: restaurant.address.addressLocality,
      ...(restaurant.address.addressRegion && {
        addressRegion: restaurant.address.addressRegion,
      }),
      postalCode: restaurant.address.postalCode,
      addressCountry: restaurant.address.addressCountry,
    },

    // GPS coordinates
    geo: {
      '@type': 'GeoCoordinates',
      latitude: restaurant.geo.latitude,
      longitude: restaurant.geo.longitude,
    },

    // Opening hours
    openingHoursSpecification: restaurant.hours.map((spec) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: spec.dayOfWeek,
      opens: spec.opens,
      closes: spec.closes,
    })),

    // Link to the menu page
    menu: `${siteUrl}/menu`,

    // Reservation support
    acceptsReservations: restaurant.features.reservations ? 'True' : 'False',

    // Social profiles
    ...(restaurant.socialLinks.length > 0 && {
      sameAs: restaurant.socialLinks.map((link) => link.url),
    }),

    // Review ratings and top testimonials
    ...(restaurant.reviews && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: restaurant.reviews.summary.rating,
        reviewCount: restaurant.reviews.summary.reviewCount,
        bestRating: 5,
        worstRating: 1,
      },
      ...(restaurant.reviews.items.length > 0 && {
        review: restaurant.reviews.items.slice(0, 5).map((r) => ({
          '@type': 'Review',
          author: {
            '@type': 'Person',
            name: r.author,
          },
          reviewRating: {
            '@type': 'Rating',
            ratingValue: r.rating,
            bestRating: 5,
            worstRating: 1,
          },
          reviewBody: r.text,
          ...(r.date && { datePublished: r.date }),
        })),
      }),
    }),
  };
}

/**
 * Generates Menu/MenuSection/MenuItem JSON-LD for the menu page.
 *
 * This tells search engines about each dish, its price, and its category.
 * This data can power "dish cards" in search results and helps AI assistants
 * answer "What pasta dishes does Trattoria Marco serve?"
 *
 * @param menu - The menu data from menu.json
 * @param restaurant - The restaurant data (for name/currency)
 * @param siteUrl - The deployed URL
 * @returns A JSON-LD object for the menu page
 */
export function generateMenuSchema(
  menu: Menu,
  restaurant: Restaurant,
  siteUrl: string,
): Record<string, unknown> {
  // Sort categories by sortOrder
  const sortedCategories = [...menu.categories].sort((a, b) => a.sortOrder - b.sortOrder);

  return {
    '@context': 'https://schema.org',
    '@type': 'Menu',
    name: `${restaurant.name} Menu`,
    url: `${siteUrl}/menu`,

    // Each category becomes a MenuSection
    hasMenuSection: sortedCategories.map((category) => {
      // Get all items in this category
      const categoryItems = menu.items.filter(
        (item) => item.categoryId === category.id && item.available,
      );

      return {
        '@type': 'MenuSection',
        name: category.name,
        ...(category.description && { description: category.description }),

        // Each item becomes a MenuItem with an Offer (price)
        hasMenuItem: categoryItems.map((item) => ({
          '@type': 'MenuItem',
          name: item.name,
          ...(item.description && { description: item.description }),
          ...(item.image && { image: item.image }),

          // Price as a schema.org Offer
          offers: {
            '@type': 'Offer',
            price: item.price.toFixed(2),
            priceCurrency: item.currency,
          },

          // Dietary restrictions as suitableForDiet
          ...(item.diets.length > 0 && {
            suitableForDiet: item.diets.map(
              (diet) => `https://schema.org/${dietToSchemaOrg(diet)}`,
            ),
          }),
        })),
      };
    }),
  };
}

/**
 * Generates a BreadcrumbList JSON-LD for navigation.
 * Helps search engines show breadcrumb trails in results.
 *
 * @param items - Array of { name, url } for each breadcrumb level
 * @returns A JSON-LD BreadcrumbList object
 */
export function generateBreadcrumbSchema(
  items: Array<{ name: string; url: string }>,
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Generates a WebSite JSON-LD for the site root.
 * Can include a SearchAction for Google sitelinks search box.
 *
 * @param name - Site name
 * @param siteUrl - Base URL
 * @param developer - Optional developer branding / attribution
 * @returns A JSON-LD WebSite object
 */
export function generateWebSiteSchema(
  name: string,
  siteUrl: string,
  developer?: DeveloperConfig,
): Record<string, unknown> {
  const devName = developer?.name ?? 'jeduapf';
  const devUrl = developer?.url ?? 'https://portfolio.jeduapf.com';
  const devRole = developer?.role ?? 'Restaurant Web Developer';

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url: siteUrl,
    creator: {
      '@type': 'Person',
      name: devName,
      url: devUrl,
      jobTitle: devRole,
      sameAs: [devUrl],
    },
  };
}

// --- Helper: Map our diet codes to schema.org RestrictedDiet values ---
function dietToSchemaOrg(diet: string): string {
  const map: Record<string, string> = {
    vegan: 'VeganDiet',
    vegetarian: 'VegetarianDiet',
    glutenFree: 'GlutenFreeDiet',
    halal: 'HalalDiet',
    kosher: 'KosherDiet',
    dairyFree: 'DairyFreeDiet',
  };
  return map[diet] ?? 'RestrictedDiet';
}
